import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Typography,
  IconButton,
  Icons,
  useTheme,
} from '@ublend-npm/aulaui-core';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import { useAllSpaces } from '../../../hooks/useAllSpaces';
import SpacesList from './SpacesList';

interface SpacesListModalProps {
  isOpen: boolean;
  onClose: () => void;
  type?: 'all' | 'starred' | 'recent';
}

const SpacesListModal: React.FC<SpacesListModalProps> = ({
  isOpen,
  onClose,
  type = 'all',
}) => {
  const { palette } = useTheme();
  const allSpacesData = useAllSpaces(type);

  // Auto-close modal if it's the starred view and the user unstars the last space
  useEffect(() => {
    if (
      isOpen &&
      type === 'starred' &&
      allSpacesData.spaces.length === 0 &&
      !allSpacesData.isLoading
    ) {
      onClose();
    }
  }, [
    isOpen,
    type,
    allSpacesData.spaces.length,
    allSpacesData.isLoading,
    onClose,
  ]);

  return (
    <Dialog
      open={isOpen}
      onClose={onClose}
      aria-labelledby="spaces-list-modal-title"
      PaperProps={{
        sx: {
          width: '648px',
          maxWidth: '100%',
          height: '85vh',
          borderRadius: '8px',
          p: '16px',
          m: 2,
          bgcolor:
            palette.mode === 'dark'
              ? 'rgba(58, 52, 68, 1)'
              : 'background.paper',
          color: 'text.primary',
          display: 'flex',
          flexDirection: 'column',
          boxSizing: 'border-box',
          gap: '10px',
        },
      }}
    >
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <Typography
          id="spaces-list-modal-title"
          variant="subtitle1"
          component="h2"
          fontWeight="medium"
          color="primary"
        >
          {type === 'starred'
            ? 'Starred spaces'
            : type === 'recent'
            ? 'Recently viewed spaces'
            : 'All spaces'}
        </Typography>
        <IconButton
          onClick={onClose}
          sx={{
            p: '5px',
            color:
              palette.mode === 'dark'
                ? 'rgba(255, 255, 255, 0.56)'
                : 'rgba(35, 31, 41, 0.56)',
          }}
          aria-label="Close modal"
        >
          <Icons.Close sx={{ width: 20, height: 20 }} />
        </IconButton>
      </Box>
      <DialogContent sx={{ p: 0, overflowY: 'auto' }}>
        <SpacesList {...allSpacesData} hideEmptyState={type === 'starred'} />
      </DialogContent>
    </Dialog>
  );
};

SpacesListModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  type: PropTypes.oneOf(['all', 'starred', 'recent']),
};

export default SpacesListModal;
